import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateImageLibraryQuery } from './useImageLibraryQuery';

interface MutationCallbacks {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: Api.IImageLibraryItem) => void;
}

/**
 * Adds an image the account already owns. Uploads and stock picks join the library at creation time via
 * the `addToLibrary` flag instead; this covers AI-generated images, which exist as attachments for every
 * generation attempt and should only be kept once the user picks one.
 */
export function useAddToImageLibraryMutation({ onError, onSuccess }: MutationCallbacks = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			fileAttachmentId,
			source,
			impersonationContext,
		}: {
			fileAttachmentId: string;
			source: Api.ImageLibrarySource;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync<Api.IImageLibraryItem>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: 'imageLibrary' }),
				'POST',
				{ fileAttachmentId, source }
			);
		},
		onError,
		onSuccess: result => {
			invalidateImageLibraryQuery();
			onSuccess?.(result);
		},
	});
}

export function useRenameImageLibraryEntryMutation({ onError, onSuccess }: MutationCallbacks = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			itemId,
			displayName,
			impersonationContext,
		}: {
			itemId: string;
			displayName: string;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync<Api.IImageLibraryItem>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: `imageLibrary/${itemId}` }),
				'PATCH',
				{ displayName }
			);
		},
		onError,
		onSuccess: result => {
			invalidateImageLibraryQuery();
			onSuccess?.(result);
		},
	});
}

/**
 * Deletes the library entry only. The attachment survives, so emails, posts, and templates that already
 * reference the image keep rendering.
 */
export function useRemoveFromImageLibraryMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			itemId,
			impersonationContext,
		}: {
			itemId: string;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: `imageLibrary/${itemId}` }),
				'DELETE'
			);
		},
		onError,
		onSuccess: () => {
			invalidateImageLibraryQuery();
			onSuccess?.();
		},
	});
}
